"use client";

import { useEffect, useRef, useState } from "react";
import { useSession } from "next-auth/react";

import { Button } from "@/components/ui/button";
import MessageCircle from "@/components/icons/MessageCircle";
import SupportChatPopup, { type GuestChatPageContext } from "@/app/categories/[categoryId]/components/SupportChatPopup";
import { useGetAllChatsQuery, useGetClientSupportGroupChatQuery } from "@/features/chat/chatApiInjection";
import { useGetSupportTicketsQuery } from "@/features/support-tickets/supportTicketApiInjection";
import { CHAT_CONSTANTS } from "@/app/profile/inbox/(chats)/constants";
import {
	getGuestLauraPendingResume,
	getStoredGuestLauraDraft,
	GUEST_LAURA_DRAFT_HANDOFF_SUCCESS_EVENT,
	OPEN_SUPPORT_CHAT_EVENT,
} from "@/lib/guest-laura-draft";
import { cn } from "@/lib/utils";
import { OnboardingStages, SupportTicketStatus, type CatalogSuggestion, type SupportTicket } from "@/types";
import { ga4ChatStart } from "@/lib/analytics/ga4-ecommerce";
import { getCtaDataAttrs } from "@/lib/analytics/cta-attrs";

const FLOATING_CONTROLS_HIDE_MAX_WIDTH = 428;
const CLOSED_BADGE_POLLING_INTERVAL = 60000;

type ChatNowFABProps = {
	pageContext?: GuestChatPageContext;
};

function playGuestMessageSound() {
	try {
		if (navigator.userActivation && !navigator.userActivation.hasBeenActive) return;
		const AudioContextCtor = window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
		if (!AudioContextCtor) return;

		const audioContext = new AudioContextCtor();
		const oscillator = audioContext.createOscillator();
		const gain = audioContext.createGain();
		const now = audioContext.currentTime;

		oscillator.type = "sine";
		oscillator.frequency.setValueAtTime(760, now);
		oscillator.frequency.exponentialRampToValueAtTime(980, now + 0.08);
		gain.gain.setValueAtTime(0.0001, now);
		gain.gain.exponentialRampToValueAtTime(0.09, now + 0.02);
		gain.gain.exponentialRampToValueAtTime(0.0001, now + 0.22);

		oscillator.connect(gain);
		gain.connect(audioContext.destination);
		oscillator.start(now);
		oscillator.stop(now + 0.24);
		window.setTimeout(() => void audioContext.close(), 320);
	} catch {
		// Browsers can block audio before user interaction; the visual nudge still shows.
	}
}

function hasUnreadUserTicketReply(ticket: SupportTicket) {
	if (ticket.status === SupportTicketStatus.CLOSED) return false;
	const latestReply = ticket.replies?.[ticket.replies.length - 1];
	if (!latestReply?.isStaffReply) return false;
	if (!ticket.userLastViewedAt) return true;
	return new Date(latestReply.createdAt).getTime() > new Date(ticket.userLastViewedAt).getTime();
}

export default function ChatNowFAB({ pageContext }: ChatNowFABProps) {
	const { data: session, status } = useSession();
	const [open, setOpen] = useState(false);
	const [catalogRequest, setCatalogRequest] = useState<{
		id: number;
		suggestions: CatalogSuggestion[];
	} | null>(null);
	const [isTinyViewport, setIsTinyViewport] = useState(false);
	const [guestNudgeReady, setGuestNudgeReady] = useState(false);
	const [guestNudgeSeen, setGuestNudgeSeen] = useState(false);
	const previousUnreadCountRef = useRef<number | null>(null);
	const resumeHandledRef = useRef(false);
	const isGuest = status === "unauthenticated";
	const userId = session?.user.id;
	const onboardingStage =
		session?.user.onboardingStages ??
		(session?.user as { onBoardingStages?: OnboardingStages } | undefined)?.onBoardingStages;
	const canUseAuthenticatedSupport =
		status === "authenticated" && onboardingStage !== OnboardingStages.SIGNUP_COMPLETED;
	const closedBadgePollingInterval = open ? 0 : CLOSED_BADGE_POLLING_INTERVAL;
	const { data: supportChat } = useGetClientSupportGroupChatQuery(undefined, {
		skip: !canUseAuthenticatedSupport,
		pollingInterval: closedBadgePollingInterval,
	});
	const { data: allChats } = useGetAllChatsQuery(
		{
			id: userId ?? "",
			page: 1,
			limit: CHAT_CONSTANTS.CHAT_LIST_LIMIT,
			archived: false,
		},
		{
			skip: !canUseAuthenticatedSupport || !userId,
			pollingInterval: closedBadgePollingInterval,
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true,
		},
	);
	const { data: supportTickets } = useGetSupportTicketsQuery(
		{
			limit: 25,
			userId,
			archived: false,
			sortBy: "lastActivityAt:desc",
		},
		{
			skip: !canUseAuthenticatedSupport || !userId,
			pollingInterval: closedBadgePollingInterval,
			refetchOnFocus: true,
			refetchOnMountOrArgChange: true,
		},
	);
	const unreadChatCount = (allChats?.docs ?? []).reduce((total, chat) => {
		if (chat._id === supportChat?._id) {
			return total + (chat.clientSupportCustomerUnreadCount ?? chat.unreadCount ?? 0);
		}
		return total + (chat.unreadCount ?? 0);
	}, supportChat && !(allChats?.docs ?? []).some((chat) => chat._id === supportChat._id)
		? (supportChat.clientSupportCustomerUnreadCount ?? supportChat.unreadCount ?? 0)
		: 0);
	const unreadTicketCount = (supportTickets?.docs ?? []).filter(hasUnreadUserTicketReply).length;
	const unreadCount = unreadChatCount + unreadTicketCount;
	const showGuestNudge = isGuest && guestNudgeReady && !guestNudgeSeen && !open;
	const showUnreadBadge = status === "authenticated" && unreadCount > 0 && !open;
	const badgeCount = showGuestNudge ? 1 : Math.min(unreadCount, 9);
	const hideFloatingButton = open && isTinyViewport;

	useEffect(() => {
		window.dispatchEvent(new CustomEvent("techxeed-support-chat-open", { detail: open }));
		return () => {
			window.dispatchEvent(new CustomEvent("techxeed-support-chat-open", { detail: false }));
		};
	}, [open]);

	useEffect(() => {
		const openSupportChat = (event: Event) => {
			const detail = (
				event as CustomEvent<{
					catalogSuggestions?: CatalogSuggestion[];
					source?: string;
					chatId?: string;
				}>
			).detail;
			const catalogSuggestions = Array.isArray(detail?.catalogSuggestions)
				? detail.catalogSuggestions
				: [];
			if (catalogSuggestions.length > 0) {
				setCatalogRequest({
					id: Date.now(),
					suggestions: catalogSuggestions,
				});
			}
			setOpen(true);
			setGuestNudgeSeen(true);
			ga4ChatStart({ source: detail?.source || "open_event" });
		};
		const handleHandoffSuccess = () => {
			resumeHandledRef.current = true;
			setOpen(true);
			setGuestNudgeSeen(true);
			ga4ChatStart({ source: "guest_laura_handoff" });
		};
		window.addEventListener(OPEN_SUPPORT_CHAT_EVENT, openSupportChat);
		window.addEventListener(GUEST_LAURA_DRAFT_HANDOFF_SUCCESS_EVENT, handleHandoffSuccess);
		return () => {
			window.removeEventListener(OPEN_SUPPORT_CHAT_EVENT, openSupportChat);
			window.removeEventListener(GUEST_LAURA_DRAFT_HANDOFF_SUCCESS_EVENT, handleHandoffSuccess);
		};
	}, []);

	// After Apple/Google/email return, reopen chat even if the success event fired on a prior page.
	useEffect(() => {
		if (!canUseAuthenticatedSupport || resumeHandledRef.current) return;

		const pendingResume = getGuestLauraPendingResume();
		const hasDraft = Boolean(getStoredGuestLauraDraft());
		if (!pendingResume && !hasDraft) return;

		resumeHandledRef.current = true;
		setOpen(true);
		setGuestNudgeSeen(true);
		ga4ChatStart({
			source: pendingResume ? "guest_laura_pending_resume" : "guest_laura_draft_resume",
		});
	}, [canUseAuthenticatedSupport]);

	useEffect(() => {
		const media = window.matchMedia(`(max-width: ${FLOATING_CONTROLS_HIDE_MAX_WIDTH}px)`);
		const updateViewport = () => {
			setIsTinyViewport(window.innerWidth <= FLOATING_CONTROLS_HIDE_MAX_WIDTH);
		};
		updateViewport();
		media.addEventListener("change", updateViewport);
		window.addEventListener("resize", updateViewport);
		return () => {
			media.removeEventListener("change", updateViewport);
			window.removeEventListener("resize", updateViewport);
		};
	}, []);

	useEffect(() => {
		if (status !== "authenticated") {
			previousUnreadCountRef.current = null;
			return;
		}

		const previousUnreadCount = previousUnreadCountRef.current;
		if (!open && previousUnreadCount !== null && unreadCount > previousUnreadCount) {
			playGuestMessageSound();
		}
		previousUnreadCountRef.current = unreadCount;
	}, [open, status, unreadCount]);

	useEffect(() => {
		if (!isGuest) {
			setGuestNudgeReady(false);
			setGuestNudgeSeen(false);
			return;
		}

		const timer = window.setTimeout(() => {
			setGuestNudgeReady(true);
			if (!open && !guestNudgeSeen) {
				playGuestMessageSound();
			}
		}, 10000);

		return () => window.clearTimeout(timer);
	}, [guestNudgeSeen, isGuest, open]);

	const toggleOpen = () => {
		setOpen((current) => {
			const next = !current;
			if (next && guestNudgeReady) setGuestNudgeSeen(true);
			if (next) ga4ChatStart({ source: "chat_fab" });
			return next;
		});
	};

	return (
		<>
			<div className={cn("fixed bottom-4 right-4 z-50 h-12 w-12", hideFloatingButton && "hidden")}>
				{showGuestNudge ? (
					<span className="pointer-events-none absolute inset-0 z-0 animate-pulse rounded-full shadow-[0_-10px_34px_hsl(var(--tertiary)_/_0.64),-12px_-2px_38px_hsl(var(--tertiary)_/_0.36),12px_-2px_38px_hsl(var(--tertiary)_/_0.36),0_0_24px_hsl(var(--tertiary)_/_0.16)]" />
				) : null}
				<Button
					type="button"
					size="icon"
					onClick={toggleOpen}
					{...getCtaDataAttrs({
						ctaId: open ? "chat_fab_close" : "chat_fab_open",
						ctaName: open ? "Close Client Support chat" : "Open Client Support chat",
						ctaLocation: "chat_fab",
					})}
					className={cn(
						"relative z-10 h-12 w-12 max-h-12 max-w-12 min-h-12 min-w-12 flex-shrink-0 items-center justify-center rounded-full shadow-custom p-0",
						(showGuestNudge || showUnreadBadge) && "chat-heartbeat",
					)}
					aria-label="Open Client Support chat"
				>
					{showGuestNudge || showUnreadBadge ? (
						<span className="absolute -left-1 -top-1 grid size-5 place-items-center rounded-full bg-tertiary text-[10px] font-bold leading-none text-white shadow-md">
							{badgeCount}
						</span>
					) : null}
					<MessageCircle 
						className="size-5 shrink-0" 
						style={{ color: "white", fill: "white" }}
					/>
				</Button>
			</div>
			<SupportChatPopup
				open={open}
				onOpenChange={setOpen}
				pageContext={pageContext}
				catalogRequest={catalogRequest}
			/>
		</>
	);
}
